/**
 * JWT Token Utilities
 *
 * Utility functions for parsing and validating JWT tokens.
 * Uses access tokens (15-minute) and refresh tokens (7-day).
 */

import type {
  AccessTokenPayload,
  RefreshTokenPayload,
  AnyTokenPayload,
} from '../../types/auth.types';
import { accessTokenPayloadSchema, refreshTokenPayloadSchema } from '../validators/auth';
import { STORAGE_KEYS, TOKEN_EXPIRY_BUFFER } from '../constants';

// ============================================================================
// TOKEN PARSING
// ============================================================================

/**
 * Parse JWT token and extract payload (unsafe - no validation)
 * @param token - JWT token string
 * @returns Parsed payload or null if parsing fails
 */
export const parseJWTUnsafe = (token: string): unknown | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    const decoded = atob(payload);
    return JSON.parse(decoded);
  } catch (error) {
    console.error('[Token] Failed to parse JWT (unsafe):', error);
    return null;
  }
};

/**
 * Parse access token
 * @param token - JWT token string
 * @returns Parsed access token payload or null if invalid
 */
export const parseAccessToken = (token: string): AccessTokenPayload | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('[Token] Invalid access token format');
      return null;
    }

    const payload = parts[1];
    const decoded = atob(payload);
    const parsed = JSON.parse(decoded);

    const validationResult = accessTokenPayloadSchema.safeParse(parsed);
    if (!validationResult.success) {
      console.error('[Token] Invalid access token payload:', validationResult.error);
      return null;
    }

    return validationResult.data;
  } catch (error) {
    console.error('[Token] Failed to parse access token:', error);
    return null;
  }
};

/**
 * Parse refresh token
 * @param token - JWT token string
 * @returns Parsed refresh token payload or null if invalid
 */
export const parseRefreshToken = (token: string): RefreshTokenPayload | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('[Token] Invalid refresh token format');
      return null;
    }

    const payload = parts[1];
    const decoded = atob(payload);
    const parsed = JSON.parse(decoded);

    const validationResult = refreshTokenPayloadSchema.safeParse(parsed);
    if (!validationResult.success) {
      console.error('[Token] Invalid refresh token payload:', validationResult.error);
      return null;
    }

    return validationResult.data;
  } catch (error) {
    console.error('[Token] Failed to parse refresh token:', error);
    return null;
  }
};

// ============================================================================
// TOKEN VALIDATION
// ============================================================================

/**
 * Check if JWT token is expired
 * @param token - JWT token string or payload with exp field
 * @returns True if expired, false otherwise
 */
export const isTokenExpired = (token: string | { exp?: number }): boolean => {
  try {
    let exp: number | undefined;

    if (typeof token === 'string') {
      const rawPayload = parseJWTUnsafe(token) as { exp?: number } | null;
      exp = rawPayload?.exp;
    } else {
      exp = token.exp;
    }

    if (!exp) {
      return true;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    return exp < currentTime;
  } catch (error) {
    console.error('[Token] Failed to check token expiration:', error);
    return true;
  }
};

/**
 * Check if token will expire soon (within specified seconds)
 * @param token - JWT token string or payload with exp field
 * @param thresholdSeconds - Seconds before expiration to consider "expiring soon"
 * @returns True if expiring soon, false otherwise
 */
export const isTokenExpiringSoon = (
  token: string | { exp?: number },
  thresholdSeconds: number = TOKEN_EXPIRY_BUFFER
): boolean => {
  try {
    let exp: number | undefined;

    if (typeof token === 'string') {
      const rawPayload = parseJWTUnsafe(token) as { exp?: number } | null;
      exp = rawPayload?.exp;
    } else {
      exp = token.exp;
    }

    if (!exp) {
      return true;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = exp - currentTime;

    return timeUntilExpiry <= thresholdSeconds;
  } catch (error) {
    console.error('[Token] Failed to check token expiration threshold:', error);
    return true;
  }
};

/**
 * Get token expiration time as Date object
 * @param token - JWT token string or payload with exp field
 * @returns Expiration date or null if invalid
 */
export const getTokenExpiry = (token: string | { exp?: number }): Date | null => {
  try {
    let exp: number | undefined;

    if (typeof token === 'string') {
      const rawPayload = parseJWTUnsafe(token) as { exp?: number } | null;
      exp = rawPayload?.exp;
    } else {
      exp = token.exp;
    }

    if (!exp) {
      return null;
    }

    return new Date(exp * 1000);
  } catch (error) {
    console.error('[Token] Failed to get token expiry:', error);
    return null;
  }
};

/**
 * Get time until token expires (in seconds)
 * @param token - JWT token string or payload with exp field
 * @returns Seconds until expiration, or 0 if expired/invalid
 */
export const getTimeUntilExpiry = (token: string | { exp?: number }): number => {
  try {
    let exp: number | undefined;

    if (typeof token === 'string') {
      const rawPayload = parseJWTUnsafe(token) as { exp?: number } | null;
      exp = rawPayload?.exp;
    } else {
      exp = token.exp;
    }

    if (!exp) {
      return 0;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = exp - currentTime;

    return Math.max(0, timeUntilExpiry);
  } catch (error) {
    console.error('[Token] Failed to get time until expiry:', error);
    return 0;
  }
};

// ============================================================================
// TOKEN STORAGE
// ============================================================================

/**
 * Token pair for storage
 */
export interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

/**
 * Save both access and refresh tokens to localStorage
 * @param accessToken - Access token string
 * @param refreshToken - Refresh token string
 */
export const saveTokenPair = (accessToken: string, refreshToken: string): void => {
  try {
    const accessPayload = parseJWTUnsafe(accessToken) as { exp?: number } | null;
    const refreshPayload = parseJWTUnsafe(refreshToken) as { exp?: number } | null;

    if (!accessPayload?.exp || !refreshPayload?.exp) {
      console.error('[Token] Cannot save invalid token pair');
      return;
    }

    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN_EXPIRY, accessPayload.exp.toString());
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN_EXPIRY, refreshPayload.exp.toString());

    console.log('[Token] Saved token pair', {
      accessExpiresIn: accessPayload.exp - Math.floor(Date.now() / 1000),
      refreshExpiresIn: refreshPayload.exp - Math.floor(Date.now() / 1000),
    });
  } catch (error) {
    console.error('[Token] Failed to save token pair:', error);
  }
};

/**
 * Save only access token to localStorage (used when admin session changes)
 * @param accessToken - Access token string
 */
export const saveAccessToken = (accessToken: string): void => {
  try {
    const accessPayload = parseJWTUnsafe(accessToken) as { exp?: number } | null;

    if (!accessPayload?.exp) {
      console.error('[Token] Cannot save invalid access token');
      return;
    }

    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN_EXPIRY, accessPayload.exp.toString());

    console.log('[Token] Saved access token', {
      accessExpiresIn: accessPayload.exp - Math.floor(Date.now() / 1000),
    });
  } catch (error) {
    console.error('[Token] Failed to save access token:', error);
  }
};

/**
 * Get access token from localStorage
 * @returns Access token string or null if not found/expired
 */
export const getAccessToken = (): string | null => {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const expiry = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN_EXPIRY);

    if (!token || !expiry) {
      return null;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    if (parseInt(expiry, 10) < currentTime) {
      // Access token expired - don't clear yet, might be able to refresh
      return null;
    }

    return token;
  } catch (error) {
    console.error('[Token] Failed to get access token:', error);
    return null;
  }
};

/**
 * Get refresh token from localStorage
 * @returns Refresh token string or null if not found/expired
 */
export const getRefreshToken = (): string | null => {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    const expiry = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN_EXPIRY);

    if (!token || !expiry) {
      return null;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    if (parseInt(expiry, 10) < currentTime) {
      clearAllTokens();
      return null;
    }

    return token;
  } catch (error) {
    console.error('[Token] Failed to get refresh token:', error);
    return null;
  }
};

/**
 * Clear all tokens from localStorage
 */
export const clearAllTokens = (): void => {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN_EXPIRY);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN_EXPIRY);
};

/**
 * Check if we have a valid refresh token (for token refresh)
 * @returns True if refresh token exists and is valid
 */
export const hasValidRefreshToken = (): boolean => {
  return !!getRefreshToken();
};

// ============================================================================
// TOKEN INFO EXTRACTION
// ============================================================================

/**
 * Extract user ID from token
 * @param token - JWT token string
 * @returns User ID or null if invalid
 */
export const getUserIdFromToken = (token: string): string | null => {
  const rawPayload = parseJWTUnsafe(token) as { sub?: string } | null;
  return rawPayload?.sub || null;
};

/**
 * Extract tenant ID from token
 * @param token - JWT token string
 * @returns Tenant ID or null if invalid
 */
export const getTenantIdFromToken = (token: string): string | null => {
  const rawPayload = parseJWTUnsafe(token) as { tenant_id?: string } | null;
  return rawPayload?.tenant_id || null;
};

/**
 * Extract tenant code from token
 * @param token - JWT token string
 * @returns Tenant code or null if invalid
 */
export const getTenantCodeFromToken = (token: string): string | null => {
  const rawPayload = parseJWTUnsafe(token) as { tenant_code?: string } | null;
  return rawPayload?.tenant_code || null;
};

/**
 * Check if user is admin from token
 * @param token - JWT token string
 * @returns True if admin, false otherwise
 */
export const isAdminFromToken = (token: string): boolean => {
  const rawPayload = parseJWTUnsafe(token) as { is_admin?: boolean } | null;
  return rawPayload?.is_admin || false;
};

/**
 * Extract email from token
 * @param token - JWT token string
 * @returns Email or null if invalid
 */
export const getEmailFromToken = (token: string): string | null => {
  const rawPayload = parseJWTUnsafe(token) as { email?: string } | null;
  return rawPayload?.email || null;
};

/**
 * Extract name from token
 * @param token - JWT token string
 * @returns Name or null if invalid
 */
export const getNameFromToken = (token: string): string | null => {
  const rawPayload = parseJWTUnsafe(token) as { name?: string } | null;
  return rawPayload?.name || null;
};

/**
 * Extract session ID from access token
 * @param token - JWT token string
 * @returns Session ID or null if not found
 */
export const getSessionIdFromToken = (token: string): string | null => {
  const rawPayload = parseJWTUnsafe(token) as { session_id?: string } | null;
  return rawPayload?.session_id || null;
};
